import { QueryClient, QueryClientProvider, type QueryClientConfig } from '@tanstack/react-query';
import { useMemo, useRef, type ReactNode } from 'react';

import { useAuthContext } from './AuthContext.js';

/**
 * Der `QueryClientProvider` beider Apps — mit einem Cache, der **an die angemeldete Identität
 * gebunden** ist.
 *
 * ### Der Fehler, den das behebt
 *
 * Abmelden verwarf bisher nur die Token. Der TanStack-Query-Cache blieb bestehen, und die
 * Query-Schlüssel (`['me']`, `['supplier-profile']`) sind global, nicht nutzerbezogen. Wer
 * sich abmeldete und als **jemand anderes** wieder anmeldete, bekam die Daten des Vorgängers
 * zu sehen. Im Lieferantenportal steht `staleTime` auf 30 Sekunden — die Antwort kam also nicht
 * kurz aufblitzend, sondern blieb stehen, bis der Cache von selbst veraltete.
 *
 * Es war **kein** Datenleck über die API: Der Request-Interceptor liest das Token bei jedem
 * Aufruf frisch aus dem Speicher, es ging also nie eine Anfrage mit fremdem Token raus. Aber
 * fremde Daten auf dem Bildschirm sind fremde Daten auf dem Bildschirm — bei einem Marktplatz,
 * dessen Versprechen „Lieferanten sehen niemals fremde Angebote" lautet, ist das die
 * unangenehmste Fehlerklasse überhaupt.
 *
 * ### Warum ein neuer Client statt `queryClient.clear()`
 *
 * `clear()` beim Abmelden wäre die naheliegende Reparatur, hätte aber zwei Schwächen: Es
 * muss an **jeder** Abmeldestelle aufgerufen werden (Portal und Admin haben eigene), und es
 * vergisst jemand. Vor allem aber löst es das Problem zum falschen Zeitpunkt — es hängt an
 * einer Aktion, nicht an der Bedingung.
 *
 * Hier hängt der Cache an der Identität: Ein Wechsel der `sub` erzeugt einen **neuen**
 * `QueryClient` mit leerem Cache, der alte wird eingesammelt. Es gibt damit keinen Zeitpunkt
 * und keinen Pfad, an dem der Cache eines Nutzers unter einem anderen sichtbar werden könnte
 * — auch nicht für einen Frame. Ein Aufruf, den man vergessen kann, existiert nicht mehr.
 *
 * ⚠️ **Muss innerhalb des `AuthProvider` stehen.** Außerhalb wirft `useAuthContext`.
 */
export function AuthQueryClientProvider({ config, children }: { config: QueryClientConfig; children: ReactNode }) {
  const { user } = useAuthContext();

  /**
   * `'anonymous'` für den abgemeldeten Zustand — sonst wäre „niemand angemeldet" derselbe
   * Schlüssel wie „noch nicht geladen", und der Cache überlebte den Abmeldevorgang.
   */
  const identity = user?.sub ?? 'anonymous';

  /**
   * Die Konfiguration liegt in einer Ref und **nicht** in den Abhängigkeiten des `useMemo`.
   *
   * Das ist kein Kunstgriff gegen den Linter, sondern die Absicherung gegen einen Aufrufer,
   * der `config` inline schreibt statt als Modulkonstante: Ein frisches Objektliteral je Render
   * würde bei jedem Durchlauf einen neuen `QueryClient` erzeugen — jede laufende Abfrage
   * begänne von vorn, und die Oberfläche hänge in einer Ladeschleife. Der Cache soll
   * ausschließlich an der Identität hängen, an sonst nichts.
   */
  const configRef = useRef(config);
  const queryClient = useMemo(() => new QueryClient(configRef.current), [identity]);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
